import { useState, useMemo, useEffect } from "react";
import { useCatalog, type CatalogProduct, type CatalogVariant } from "@/data/CatalogContext";
import * as CatalogService from "@/services/catalog.service";
import * as ProductRegistryService from "@/services/productRegistry.service";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Package, Plus, Pencil, Trash2, Image, ChevronRight, Search,
  Layers, Upload, WarehouseIcon, Eye, Download, History, ArrowUp, ArrowDown, MoreHorizontal, Check, ChevronsUpDown,
} from "lucide-react";
import { useCatalogPermissions } from "@/hooks/useCatalogPermissions";
import { usePermissions } from "@/data/PermissionsContext";
import { useProductRegistry, type ProductCategory, type RegistryProduct } from "@/data/ProductRegistryContext";
import CatalogImportWizard, { ImportHistoryDialog, downloadCsvTemplate, exportCurrentCatalog } from "@/components/CatalogImportWizard";
import type { ImportBatch } from "@/data/catalogImportTypes";

/* ═══════════════════════════════════════════════════════
   Offerings Catalog Admin — Enhanced with dual images,
   variant vendor/price/description fields
   ═══════════════════════════════════════════════════════ */

export default function OfferingsCatalog() {
  const { categories, products, setProducts, refreshFromDb, isDbConnected, effectiveCategories, dbProducts, isLoading: isCatalogLoading, hasLoadedFromDb, getCategoryIdBySlug } = useCatalog();
  const { products: registryProducts, refreshProducts: refreshRegistryProducts } = useProductRegistry();
  const catalogPerms = useCatalogPermissions();
  const { hasPermission: hasPerm } = usePermissions();
  const [selectedCategorySlug, setSelectedCategorySlug] = useState(effectiveCategories[0]?.slug || "wall_system");
  const [searchQuery, setSearchQuery] = useState("");
  const [editingProduct, setEditingProduct] = useState<CatalogProduct | null>(null);
  const [editingVariant, setEditingVariant] = useState<{ product: CatalogProduct; variant: CatalogVariant | null } | null>(null);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [editingCategory, setEditingCategory] = useState<{
    id: string;
    name: string;
    slug: string;
    description: string;
    icon: string;
    sortOrder: string;
  } | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<{ id: string; name: string } | null>(null);
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [showImportHistory, setShowImportHistory] = useState(false);
  const [importBatches, setImportBatches] = useState<ImportBatch[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateRegistrySku, setShowCreateRegistrySku] = useState(false);
  const [registrySkuDraft, setRegistrySkuDraft] = useState({
    sku: "",
    productName: "",
    vendor: "",
    cost: "",
    price: "",
    category: "Other" as ProductCategory,
  });
  const [registryCreateTarget, setRegistryCreateTarget] = useState<{ productId: string; variantId: string } | null>(null);

  const [newProduct, setNewProduct] = useState({
    registryProductId: "",
    displayName: "",
    sku: "",
    notes: "",
    productKind: "single" as "single" | "set" | "bundle",
    pricingImpact: "Included",
    vendor: "",
    materialFilter: "",
  });
  const [newVariant, setNewVariant] = useState({
    name: "",
    imageUrl: "",
    referenceImageUrl: "",
    sku: "",
    vendor: "",
    cost: "",
    basePrice: "",
    description: "",
    items: [] as { sku: string; qty: number }[],
    selectedItemSku: "__none",
  });
  const [variantSelectedItemSku, setVariantSelectedItemSku] = useState<Record<string, string>>({});
  const [variantItemPickerOpen, setVariantItemPickerOpen] = useState<Record<string, boolean>>({});
  const [variantSkuPickerOpen, setVariantSkuPickerOpen] = useState<Record<string, boolean>>({});
  const [newVariantItemPickerOpen, setNewVariantItemPickerOpen] = useState(false);
  const [newVariantSkuPickerOpen, setNewVariantSkuPickerOpen] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: "",
    slug: "",
    description: "",
    icon: "Package",
    sortOrder: "",
  });

  useEffect(() => {
    refreshFromDb();
  }, [refreshFromDb]);

  useEffect(() => {
    if (!categories.length) return;
    if (!categories.some((category) => category.slug === selectedCategorySlug)) {
      setSelectedCategorySlug(categories[0].slug);
    }
  }, [categories, selectedCategorySlug]);

  const selectedCategory = categories.find(c => c.slug === selectedCategorySlug);
  const dbProductIds = useMemo(() => new Set(dbProducts.map((product) => product.id)), [dbProducts]);
  const activeRegistryProducts = useMemo(
    () => registryProducts.filter((product) => product.active && product.sku),
    [registryProducts],
  );
  const registryBySku = useMemo(
    () => new Map(activeRegistryProducts.map((product) => [product.sku, product])),
    [activeRegistryProducts],
  );
  const registryById = useMemo(
    () => new Map(activeRegistryProducts.map((product) => [product.productId, product])),
    [activeRegistryProducts],
  );
  const registrySelectionOptions = useMemo(
    () => [...activeRegistryProducts].sort((a, b) => a.productName.localeCompare(b.productName)),
    [activeRegistryProducts],
  );
  const selectedRegistryProductForDraft = useMemo(
    () => (newProduct.registryProductId ? registryById.get(newProduct.registryProductId) ?? null : null),
    [newProduct.registryProductId, registryById],
  );
  const getProductTypeBadgeClass = (kind?: "single" | "set" | "bundle") => {
    switch (kind || "single") {
      case "set":
        return "border-blue-500/40 text-blue-300 bg-blue-500/10";
      case "bundle":
        return "border-amber-500/40 text-amber-300 bg-amber-500/10";
      default:
        return "border-border text-muted-foreground";
    }
  };
  const slugToken = (value: string) =>
    value
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  const buildGeneratedVariantSku = (productName: string, variantName: string) => {
    const p = slugToken(productName).slice(0, 18);
    const v = slugToken(variantName).slice(0, 18);
    return [p, v].filter(Boolean).join("-") || `VAR-${Date.now()}`;
  };

  const resetNewProductDraft = () => {
    setNewProduct({
      registryProductId: "",
      displayName: "",
      sku: "",
      notes: "",
      productKind: "single",
      pricingImpact: "Included",
      vendor: "",
      materialFilter: "",
    });
  };

  useEffect(() => {
    if (!newProduct.registryProductId) return;
    const selected = registryById.get(newProduct.registryProductId);
    if (!selected) return;

    setNewProduct((prev) => ({
      ...prev,
      displayName: selected.productName || prev.displayName,
      sku: selected.sku || "",
      vendor: selected.vendor || "",
      pricingImpact: selected.price > 0 ? `+$${selected.price}` : "Included",
    }));
  }, [newProduct.registryProductId, registryById]);

  const filteredProducts = useMemo(() => {
    return products
      .filter(p => p.categorySlug === selectedCategorySlug)
      .filter(p => !searchQuery || p.displayName.toLowerCase().includes(searchQuery.toLowerCase()))
      .sort((a, b) => a.sortOrder - b.sortOrder || a.displayName.localeCompare(b.displayName));
  }, [products, selectedCategorySlug, searchQuery]);

  const sortedActiveCategories = useMemo(
    () => categories.filter((c) => c.active).sort((a, b) => a.sortOrder - b.sortOrder),
    [categories],
  );

  const categoryCounts = useMemo(() => {
    const counts: Record<string, { total: number; active: number }> = {};
    categories.forEach(c => {
      const catProducts = products.filter(p => p.categorySlug === c.slug);
      counts[c.slug] = { total: catProducts.length, active: catProducts.filter(p => p.active).length };
    });
    return counts;
  }, [products, categories]);

  const inferRegistryCategoryFromCatalogSlug = (slug: string): ProductCategory => {
    if (slug === "wall_system") return "Wall System";
    if (slug === "base") return "Base";
    if (slug.includes("glass") || slug.includes("enclosure")) return "Glass/Enclosure";
    if (slug.includes("hardware")) return "Hardware";
    if (slug.includes("add")) return "Add-On";
    return "Other";
  };

  const handleAddCategory = async () => {
    const name = newCategory.name.trim();
    if (!name) {
      toast.error("Category name is required");
      return;
    }

    const normalizedSlug = (newCategory.slug.trim() || name)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");

    if (!normalizedSlug) {
      toast.error("Invalid category slug");
      return;
    }

    const sortOrderInput = Number.parseInt(newCategory.sortOrder, 10);
    const nextSortOrder =
      Number.isFinite(sortOrderInput) && sortOrderInput >= 0
        ? sortOrderInput
        : Math.max(0, ...categories.map((c) => c.sortOrder ?? 0)) + 1;

    try {
      setIsLoading(true);
      await CatalogService.createCategory({
        name,
        slug: normalizedSlug,
        description: newCategory.description.trim() || null,
        icon: newCategory.icon.trim() || "Package",
        sort_order: nextSortOrder,
        active: true,
      });
      await refreshFromDb();
      setSelectedCategorySlug(normalizedSlug);
      setShowAddCategory(false);
      setNewCategory({ name: "", slug: "", description: "", icon: "Package", sortOrder: "" });
      toast.success("Category created", { description: name });
    } catch (err) {
      console.error("Failed to create category:", err);
      toast.error("Failed to create category", { description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory) return;
    const name = editingCategory.name.trim();
    if (!name) {
      toast.error("Category name is required");
      return;
    }
    const normalizedSlug = (editingCategory.slug.trim() || name)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    if (!normalizedSlug) {
      toast.error("Invalid category slug");
      return;
    }

    const sortOrderInput = Number.parseInt(editingCategory.sortOrder, 10);
    const nextSortOrder = Number.isFinite(sortOrderInput) && sortOrderInput >= 0 ? sortOrderInput : 0;

    try {
      setIsLoading(true);
      await CatalogService.updateCategory(editingCategory.id, {
        name,
        slug: normalizedSlug,
        description: editingCategory.description.trim() || null,
        icon: editingCategory.icon.trim() || "Package",
        sort_order: nextSortOrder,
      });
      await refreshFromDb();
      if (selectedCategorySlug === categories.find((c) => c.id === editingCategory.id)?.slug) {
        setSelectedCategorySlug(normalizedSlug);
      }
      toast.success("Category updated", { description: name });
      setEditingCategory(null);
    } catch (err) {
      console.error("Failed to update category:", err);
      toast.error("Failed to update category", { description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!deletingCategory) return;
    const usage = categoryCounts[categories.find((c) => c.id === deletingCategory.id)?.slug || ""]?.total || 0;
    if (usage > 0) {
      toast.error("Cannot delete category", { description: "Move or delete products in this category first." });
      return;
    }

    try {
      setIsLoading(true);
      const deletedId = deletingCategory.id;
      await CatalogService.deleteCategory(deletedId);
      await refreshFromDb();
      const remaining = categories.filter((c) => c.id !== deletedId);
      if (remaining.length > 0 && categories.find((c) => c.id === deletedId)?.slug === selectedCategorySlug) {
        setSelectedCategorySlug(remaining[0].slug);
      }
      toast.success("Category deleted", { description: deletingCategory.name });
      setDeletingCategory(null);
    } catch (err) {
      console.error("Failed to delete category:", err);
      toast.error("Failed to delete category", { description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMoveCategory = async (categoryId: string, direction: "up" | "down") => {
    const index = sortedActiveCategories.findIndex((c) => c.id === categoryId);
    if (index < 0) return;
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= sortedActiveCategories.length) return;

    const current = sortedActiveCategories[index];
    const target = sortedActiveCategories[targetIndex];
    if (!current || !target) return;

    try {
      setIsLoading(true);
      await Promise.all([
        CatalogService.updateCategory(current.id, { sort_order: target.sortOrder }),
        CatalogService.updateCategory(target.id, { sort_order: current.sortOrder }),
      ]);
      await refreshFromDb();
      toast.success("Category order updated");
    } catch (err) {
      console.error("Failed to reorder categories:", err);
      toast.error("Failed to reorder categories", { description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setIsLoading(false);
    }
  };

  /* ── Product CRUD ── */
  const toggleProductActive = async (productId: string, currentActive: boolean) => {
    if (!isDbConnected || !dbProductIds.has(productId)) {
      setProducts(prev => prev.map(p => p.id === productId ? { ...p, active: !currentActive } : p));
      return;
    }
    try {
      setIsLoading(true);
      await CatalogService.updateProduct(productId, { is_active: !currentActive });
      await refreshFromDb();
    } catch (err) {
      console.error("Failed to toggle product active status:", err);
      // Fallback to local state
      setProducts(prev => prev.map(p => p.id === productId ? { ...p, active: !currentActive } : p));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddProduct = async () => {
    if (!newProduct.displayName.trim()) return;
    const selectedRegistryProduct = newProduct.registryProductId
      ? registryById.get(newProduct.registryProductId)
      : null;
    if (!selectedRegistryProduct) {
      toast.error("Select a Product Registry item first");
      return;
    }
    
    // Check if database is connected and accessible
    if (!isDbConnected) {
      // Fallback to local state only
      const id = `prod-${Date.now()}`;
      const maxSort = Math.max(0, ...products.filter(p => p.categorySlug === selectedCategorySlug).map(p => p.sortOrder));
      const product: CatalogProduct = {
        id, displayName: newProduct.displayName, categorySlug: selectedCategorySlug,
        productKind: newProduct.productKind,
        materialFilter: newProduct.materialFilter || undefined, active: true, sortOrder: maxSort + 1,
        notes: newProduct.notes, pricingImpact: newProduct.pricingImpact, vendor: newProduct.vendor,
        skus: selectedRegistryProduct.sku ? [selectedRegistryProduct.sku] : [], variants: [],
      };
      setProducts(prev => [...prev, product]);
      resetNewProductDraft();
      setShowAddProduct(false);
      toast.success("Product added", { description: newProduct.displayName });
      return;
    }

    try {
      setIsLoading(true);
      
      // Get the category ID from DB categories (not seed categories)
      const categoryId = getCategoryIdBySlug(selectedCategorySlug);
      if (!categoryId) {
        toast.error("Category unavailable", { description: "Catalog categories have not finished loading from the database." });
        return;
      }
      
      await CatalogService.createProduct({
        name: newProduct.displayName,
        description: newProduct.notes || null,
        category_id: categoryId,
        product_type: newProduct.productKind,
        base_price: newProduct.pricingImpact === "Included" ? 0 : newProduct.pricingImpact.startsWith("+$") 
          ? parseFloat(newProduct.pricingImpact.replace(/[+$]/g, "")) || null 
          : null,
        is_active: true,
        is_featured: false,
        unit_of_measure: "unit",
        min_order_quantity: 1,
        lead_time_days: 0,
        tags: null,
        specifications: newProduct.materialFilter
          ? { materialFilter: newProduct.materialFilter }
          : null,
        sku: selectedRegistryProduct.sku || null,
        cost_price: selectedRegistryProduct.cost ?? null,
      });
      
      await refreshFromDb();
      resetNewProductDraft();
      setShowAddProduct(false);
      toast.success("Product added", { description: newProduct.displayName });
    } catch (err) {
      console.error("Failed to add product - full error:", err);
      // Show error toast so user sees what went wrong
      let errorMessage = "Failed to add product to database";
      if (err && typeof err === 'object' && 'message' in err) {
        errorMessage = String((err as { message: unknown }).message);
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      toast.error("Database error", { description: errorMessage });
      setIsLoading(false);
      return;
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateProduct = async () => {
    if (!editingProduct) return;

    if (!isDbConnected || !dbProductIds.has(editingProduct.id)) {
      setProducts(prev => prev.map(p => p.id === editingProduct.id ? editingProduct : p));
      setEditingProduct(null);
      toast.success("Product updated", { description: editingProduct.displayName });
      return;
    }

    try {
      setIsLoading(true);
      
      // Find the category ID from the slug
      const category = effectiveCategories.find(c => c.slug === editingProduct.categorySlug);
      
      await CatalogService.updateProduct(editingProduct.id, {
        name: editingProduct.displayName,
        description: editingProduct.notes || null,
        category_id: category?.id || null,
        product_type: editingProduct.productKind || "single",
        base_price: editingProduct.pricingImpact === "Included" ? 0 : editingProduct.pricingImpact.startsWith("+$") 
          ? parseFloat(editingProduct.pricingImpact.replace(/[+$]/g, "")) || null 
          : null,
        specifications: editingProduct.materialFilter
          ? { materialFilter: editingProduct.materialFilter }
          : null,
      });
      
      await refreshFromDb();
      setEditingProduct(null);
      toast.success("Product updated", { description: editingProduct.displayName });
    } catch (err) {
      console.error("Failed to update product:", err);
      // Fallback to local state
      setProducts(prev => prev.map(p => p.id === editingProduct.id ? editingProduct : p));
      setEditingProduct(null);
      toast.error("Failed to update product", { description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    if (!isDbConnected || !dbProductIds.has(productId)) {
      const productName = products.find(p => p.id === productId)?.displayName || productId;
      setProducts(prev => prev.filter(p => p.id !== productId));
      toast.success("Product deleted", { description: productName });
      return;
    }

    try {
      setIsLoading(true);
      const productName = products.find(p => p.id === productId)?.displayName || productId;
      await CatalogService.deleteProduct(productId);
      await refreshFromDb();
      toast.success("Product deleted", { description: productName });
    } catch (err) {
      console.error("Failed to delete product:", err);
      // Fallback to local state
      const productName = products.find(p => p.id === productId)?.displayName || productId;
      setProducts(prev => prev.filter(p => p.id !== productId));
      toast.error("Failed to delete product", { description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setIsLoading(false);
    }
  };

  /* ── Variant CRUD ── */
  const resetNewVariant = () =>
    setNewVariant({
      name: "",
      imageUrl: "",
      referenceImageUrl: "",
      sku: "",
      vendor: "",
      cost: "",
      basePrice: "",
      description: "",
      items: [],
      selectedItemSku: "__none",
    });

  const handleAddVariant = async () => {
    if (!editingVariant) return;
    const product = editingVariant.product;

    const parsedBasePrice = newVariant.basePrice ? parseFloat(newVariant.basePrice) : null;
    const parsedCost = newVariant.cost ? parseFloat(newVariant.cost) : null;
    const effectiveCost = parsedCost ?? parsedBasePrice;
    const isComposite = product.productKind === "set" || product.productKind === "bundle";
    const effectiveSku = isComposite
      ? (newVariant.sku || buildGeneratedVariantSku(product.displayName, newVariant.name))
      : newVariant.sku;

    if (!isDbConnected || !dbProductIds.has(product.id)) {
      const maxSort = Math.max(0, ...product.variants.map(v => v.sortOrder));
      const variant: CatalogVariant = {
        id: `var-${Date.now()}`,
        productId: product.id,
        name: newVariant.name,
        imageUrl: newVariant.imageUrl,
        referenceImageUrl: newVariant.referenceImageUrl,
        active: true,
        sortOrder: maxSort + 1,
        sku: effectiveSku,
        vendor: newVariant.vendor,
        cost: effectiveCost,
        basePrice: parsedBasePrice,
        description: newVariant.description,
        items: newVariant.items,
      };
      setProducts(prev => prev.map(p =>
        p.id === product.id ? { ...p, variants: [...p.variants, variant] } : p
      ));
      resetNewVariant();
      setEditingVariant(prev => prev ? { ...prev, product: { ...prev.product, variants: [...prev.product.variants, variant] } } : null);
      toast.success("Variant added", { description: newVariant.name });
      return;
    }

    try {
      setIsLoading(true);
      await CatalogService.createVariant({
        product_id: product.id,
        name: newVariant.name,
        sku: effectiveSku || null,
        color: newVariant.description || null,
        price_adjustment: parsedBasePrice ?? 0,
        cost_adjustment: effectiveCost ?? 0,
        is_active: true,
        sort_order: product.variants.length + 1,
        specifications: { items: newVariant.items },
        size: null,
        material: null,
        finish: null,
        sku_code: null,
        weight_kg: null,
      });
      await refreshFromDb();
      resetNewVariant();
      // Refresh the editing variant dialog
      const updatedProduct = products.find(p => p.id === product.id);
      if (updatedProduct) {
        setEditingVariant({ product: updatedProduct, variant: null });
      }
      toast.success("Variant added", { description: newVariant.name });
    } catch (err) {
      console.error("Failed to add variant:", err);
      toast.error("Failed to add variant", { description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleVariantActive = async (productId: string, variantId: string, currentActive: boolean) => {
    if (!isDbConnected || !dbProductIds.has(productId)) {
      setProducts(prev => prev.map(p =>
        p.id === productId ? { ...p, variants: p.variants.map(v => v.id === variantId ? { ...v, active: !currentActive } : v) } : p
      ));
      return;
    }
    try {
      setIsLoading(true);
      await CatalogService.updateVariant(variantId, { is_active: !currentActive });
      await refreshFromDb();
      toast.success("Variant updated");
    } catch (err) {
      console.error("Failed to toggle variant active status:", err);
      // Fallback to local state
      setProducts(prev => prev.map(p =>
        p.id === productId ? { ...p, variants: p.variants.map(v => v.id === variantId ? { ...v, active: !currentActive } : v) } : p
      ));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteVariant = async (productId: string, variantId: string) => {
    if (!isDbConnected || !dbProductIds.has(productId)) {
      const variantName = products.find(p => p.id === productId)?.variants.find(v => v.id === variantId)?.name || variantId;
      setProducts(prev => prev.map(p =>
        p.id === productId ? { ...p, variants: p.variants.filter(v => v.id !== variantId) } : p
      ));
      toast.success("Variant deleted", { description: variantName });
      return;
    }
    try {
      setIsLoading(true);
      const variantName = products.find(p => p.id === productId)?.variants.find(v => v.id === variantId)?.name || variantId;
      await CatalogService.deleteVariant(variantId);
      await refreshFromDb();
      toast.success("Variant deleted", { description: variantName });
    } catch (err) {
      console.error("Failed to delete variant:", err);
      // Fallback to local state
      const variantName = products.find(p => p.id === productId)?.variants.find(v => v.id === variantId)?.name || variantId;
      setProducts(prev => prev.map(p =>
        p.id === productId ? { ...p, variants: p.variants.filter(v => v.id !== variantId) } : p
      ));
      toast.error("Failed to delete variant", { description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setIsLoading(false);
    }
  };

  const updateVariantField = (
    productId: string,
    variantId: string,
    field: keyof CatalogVariant,
    value: string | number | { sku: string; qty: number }[] | null,
  ) => {
    setProducts(prev => prev.map(p =>
      p.id === productId ? { ...p, variants: p.variants.map(v => v.id === variantId ? { ...v, [field]: value } : v) } : p
    ));
  };

  const addItemToNewVariant = () => {
    const sku = newVariant.selectedItemSku;
    if (!sku || sku === "__none") return;
    if (!registryBySku.has(sku)) {
      toast.error("Invalid item SKU", { description: "Select a product from Product Registry." });
      return;
    }
    const existing = newVariant.items.find((item) => item.sku === sku);
    if (existing) {
      setNewVariant((prev) => ({
        ...prev,
        items: prev.items.map((item) =>
          item.sku === sku ? { ...item, qty: item.qty + 1 } : item,
        ),
        selectedItemSku: "__none",
      }));
      return;
    }
    setNewVariant((prev) => ({
      ...prev,
      items: [...prev.items, { sku, qty: 1 }],
      selectedItemSku: "__none",
    }));
  };

  const removeItemFromNewVariant = (sku: string) => {
    setNewVariant((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.sku !== sku),
    }));
  };

  const adjustNewVariantItemQty = (sku: string, delta: number) => {
    setNewVariant((prev) => ({
      ...prev,
      items: prev.items
        .map((item) =>
          item.sku === sku ? { ...item, qty: Math.max(0, item.qty + delta) } : item,
        )
        .filter((item) => item.qty > 0),
    }));
  };

  const addItemToVariant = (productId: string, variantId: string) => {
    const selectedSku = variantSelectedItemSku[variantId];
    if (!selectedSku || selectedSku === "__none") return;
    if (!registryBySku.has(selectedSku)) {
      toast.error("Invalid item SKU", { description: "Select a product from Product Registry." });
      return;
    }
    const product = products.find((item) => item.id === productId);
    const variant = product?.variants.find((item) => item.id === variantId);
    if (!variant) return;
    const existing = variant.items.find((item) => item.sku === selectedSku);
    if (existing) {
      updateVariantField(
        productId,
        variantId,
        "items",
        variant.items.map((item) =>
          item.sku === selectedSku ? { ...item, qty: item.qty + 1 } : item,
        ),
      );
      setVariantSelectedItemSku((prev) => ({ ...prev, [variantId]: "__none" }));
      return;
    }
    updateVariantField(productId, variantId, "items", [...variant.items, { sku: selectedSku, qty: 1 }]);
    setVariantSelectedItemSku((prev) => ({ ...prev, [variantId]: "__none" }));
  };

  const removeItemFromVariant = (productId: string, variantId: string, sku: string) => {
    const product = products.find((item) => item.id === productId);
    const variant = product?.variants.find((item) => item.id === variantId);
    if (!variant) return;
    updateVariantField(
      productId,
      variantId,
      "items",
      variant.items.filter((item) => item.sku !== sku),
    );
  };

  const adjustVariantItemQty = (productId: string, variantId: string, sku: string, delta: number) => {
    const product = products.find((item) => item.id === productId);
    const variant = product?.variants.find((item) => item.id === variantId);
    if (!variant) return;
    updateVariantField(
      productId,
      variantId,
      "items",
      variant.items
        .map((item) =>
          item.sku === sku ? { ...item, qty: Math.max(0, item.qty + delta) } : item,
        )
        .filter((item) => item.qty > 0),
    );
  };

  const persistVariantItems = async (productId: string, variantId: string) => {
    const product = products.find((item) => item.id === productId);
    const variant = product?.variants.find((item) => item.id === variantId);
    if (!variant) return;

    if (!isDbConnected || !dbProductIds.has(productId)) {
      toast.success("Variant items updated", { description: variant.name });
      return;
    }

    try {
      setIsLoading(true);
      await CatalogService.updateVariant(variantId, {
        specifications: {
          items: variant.items ?? [],
        },
      });
      await refreshFromDb();
      toast.success("Variant items saved", { description: variant.name });
    } catch (err) {
      console.error("Failed to save variant items:", err);
      toast.error("Failed to save variant items", {
        description: err instanceof Error ? err.message : "Unknown error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const applyRegistrySkuToVariant = (productId: string, variantId: string, sku: string) => {
    const registryProduct = registryBySku.get(sku);
    setProducts((prev) =>
      prev.map((product) =>
        product.id === productId
          ? {
              ...product,
              variants: product.variants.map((variant) =>
                variant.id === variantId
                  ? {
                      ...variant,
                      sku,
                      vendor: registryProduct?.vendor || variant.vendor,
                      cost: registryProduct?.cost ?? variant.cost,
                      basePrice: registryProduct?.price ?? variant.basePrice,
                    }
                  : variant,
              ),
            }
          : product,
      ),
    );
  };

  const startCreateRegistrySku = (target: { productId: string; variantId: string }, variantName: string, skuHint: string) => {
    const product = products.find((item) => item.id === target.productId);
    setRegistryCreateTarget(target);
    setRegistrySkuDraft({
      sku: (skuHint || "").trim().toUpperCase(),
      productName: variantName || product?.displayName || "",
      vendor: "",
      cost: "",
      price: "",
      category: inferRegistryCategoryFromCatalogSlug(product?.categorySlug || ""),
    });
    setShowCreateRegistrySku(true);
  };

  const handleCreateRegistrySku = async () => {
    const sku = registrySkuDraft.sku.trim().toUpperCase();
    const productName = registrySkuDraft.productName.trim();
    if (!sku || !productName) {
      toast.error("Missing required fields", { description: "SKU and Product Name are required." });
      return;
    }
    const payload: RegistryProduct = {
      productId: crypto.randomUUID(),
      sku,
      productName,
      vendor: registrySkuDraft.vendor.trim() || "Catalog Vendor",
      category: registrySkuDraft.category,
      subcategory: "",
      unitOfMeasure: "each",
      cost: registrySkuDraft.cost ? Number(registrySkuDraft.cost) : 0,
      price: registrySkuDraft.price ? Number(registrySkuDraft.price) : 0,
      trackInventory: true,
      stockQuantity: 0,
      reorderThreshold: 1,
      active: true,
      notes: "Created from Offerings Catalog variant flow.",
    };
    try {
      setIsLoading(true);
      await ProductRegistryService.createProduct(payload);
      await refreshRegistryProducts();
      if (registryCreateTarget && registryCreateTarget.variantId !== "__new__") {
        applyRegistrySkuToVariant(registryCreateTarget.productId, registryCreateTarget.variantId, sku);
      } else {
        setNewVariant((prev) => ({
          ...prev,
          sku,
          vendor: payload.vendor,
          cost: String(payload.cost),
          basePrice: String(payload.price),
        }));
      }
      toast.success("SKU registered", { description: `${sku} is now available in Product Registry.` });
      setShowCreateRegistrySku(false);
      setRegistryCreateTarget(null);
    } catch (err) {
      console.error("Failed to create product registry item:", err);
      toast.error("Failed to register SKU", { description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground">Offerings Catalog</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Manage products, variants, images, and SKUs. Changes reflect immediately in Sales, Ordering, and Warehouse.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {catalogPerms.edit && (
            <>
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={() => exportCurrentCatalog(products, categories)}>
                <Download className="h-3.5 w-3.5" /> Export
              </Button>
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={() => setShowImportWizard(true)}>
                <Upload className="h-3.5 w-3.5" /> Import CSV
              </Button>
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={() => setShowImportHistory(true)}>
                <History className="h-3.5 w-3.5" /> History
              </Button>
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={() => setShowAddCategory(true)}>
                <Layers className="h-3.5 w-3.5" /> Add Category
              </Button>
            </>
          )}
          <Badge variant="outline" className="text-[10px]">
            {products.filter(p => p.active).length} active products
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Left — Category Nav */}
        <div className="col-span-3">
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Categories</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {isCatalogLoading ? (
                <div className="space-y-0.5 pb-2 px-4 py-2">
                  {[1, 2, 3, 4, 5, 6].map(i => (
                    <div key={i} className="flex items-center justify-between py-2.5">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-4 w-8" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-1 pb-2">
                  {sortedActiveCategories.map((cat, idx) => {
                    const counts = categoryCounts[cat.slug] || { total: 0, active: 0 };
                    const isActive = selectedCategorySlug === cat.slug;
                    return (
                      <div
                        key={cat.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedCategorySlug(cat.slug)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setSelectedCategorySlug(cat.slug);
                          }
                        }}
                        className={`group w-full flex items-center justify-between px-4 py-3 text-left transition-colors rounded-r-md ${
                          isActive ? "bg-primary/10 text-primary border-l-2 border-primary" : "hover:bg-muted/50 border-l-2 border-transparent"
                        }`}>
                        <span className="text-sm font-semibold pr-2">{cat.name}</span>
                        <div className="flex items-center gap-1.5 min-w-0">
                          {catalogPerms.edit && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0 opacity-50 group-hover:opacity-100 transition-opacity"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <MoreHorizontal className="h-3.5 w-3.5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-44">
                                <DropdownMenuItem
                                  disabled={isLoading || idx === 0}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void handleMoveCategory(cat.id, "up");
                                  }}
                                >
                                  <ArrowUp className="h-3.5 w-3.5 mr-2" />
                                  Move Up
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  disabled={isLoading || idx === sortedActiveCategories.length - 1}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void handleMoveCategory(cat.id, "down");
                                  }}
                                >
                                  <ArrowDown className="h-3.5 w-3.5 mr-2" />
                                  Move Down
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingCategory({
                                      id: cat.id,
                                      name: cat.name,
                                      slug: cat.slug,
                                      description: cat.description || "",
                                      icon: cat.icon || "Package",
                                      sortOrder: String(cat.sortOrder ?? 0),
                                    });
                                  }}
                                >
                                  <Pencil className="h-3.5 w-3.5 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeletingCategory({ id: cat.id, name: cat.name });
                                  }}
                                >
                                  <Trash2 className="h-3.5 w-3.5 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                          <Badge variant={isActive ? "default" : "secondary"} className="text-[10px] h-5 px-2 shrink-0">
                            {counts.active}/{counts.total}
                          </Badge>
                          <ChevronRight className="h-3 w-3 text-muted-foreground" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right — Products */}
        <div className="col-span-9 space-y-3">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input className="h-9 pl-9 text-xs" placeholder={`Search ${selectedCategory?.name || "products"}...`}
                value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
            </div>
            {catalogPerms.edit && (
              <Button size="sm" className="h-9 text-xs gap-1.5" onClick={() => setShowAddProduct(true)}>
                <Plus className="h-3.5 w-3.5" /> Add Product
              </Button>
            )}
          </div>

          <div className="space-y-2">
            {!isCatalogLoading && hasLoadedFromDb && filteredProducts.map(product => (
              <Card key={product.id} className={`transition-all ${!product.active ? "opacity-50" : ""}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Package className="h-4 w-4 text-primary shrink-0" />
                        <span className="text-sm font-bold text-foreground">{product.displayName}</span>
                        <Badge
                          variant="outline"
                          className={`text-[9px] h-4 uppercase tracking-wide ${getProductTypeBadgeClass(product.productKind)}`}
                        >
                          {product.productKind || "single"}
                        </Badge>
                        {product.vendor && <Badge variant="outline" className="text-[9px] h-4">{product.vendor}</Badge>}
                        {product.materialFilter && <Badge variant="secondary" className="text-[9px] h-4">{product.materialFilter}</Badge>}
                        <Badge variant={product.pricingImpact === "Included" ? "default" : "outline"} className="text-[9px] h-4">{product.pricingImpact}</Badge>
                      </div>
                      {product.notes && <p className="text-[11px] text-muted-foreground mb-2">{product.notes}</p>}
                      {product.variants.length > 0 && (
                        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                          <span className="text-[10px] text-muted-foreground font-medium mr-1">
                            {product.variants.filter(v => v.active).length} variants:
                          </span>
                          {product.variants.filter(v => v.active).slice(0, 8).map(v => (
                            <div key={v.id} className="flex items-center gap-1">
                              {v.imageUrl ? (
                                <img src={v.imageUrl} alt={v.name} className="h-6 w-6 rounded border border-border object-cover" />
                              ) : (
                                <div className="h-6 w-6 rounded border border-border bg-muted flex items-center justify-center">
                                  <Image className="h-3 w-3 text-muted-foreground" />
                                </div>
                              )}
                            </div>
                          ))}
                          {product.variants.filter(v => v.active).length > 8 && (
                            <span className="text-[10px] text-muted-foreground">+{product.variants.filter(v => v.active).length - 8} more</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {catalogPerms.edit && (
                        <>
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-muted-foreground">{product.active ? "Active" : "Inactive"}</span>
                            <Switch checked={product.active} onCheckedChange={() => toggleProductActive(product.id, product.active)} />
                          </div>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0"
                            onClick={() => setEditingVariant({ product, variant: null })}>
                            <Layers className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0"
                            onClick={() => setEditingProduct({ ...product })}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive">
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Product?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete "{product.displayName}"? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDeleteProduct(product.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {filteredProducts.length === 0 && !isCatalogLoading && hasLoadedFromDb && (
              <div className="text-center py-12 text-muted-foreground">
                <Package className="h-8 w-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm font-medium">No products in this category</p>
                <p className="text-xs mt-1">Add a product to get started.</p>
              </div>
            )}
            {(isCatalogLoading || (!hasLoadedFromDb && !isCatalogLoading)) && (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map(i => (
                  <Card key={i}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Skeleton className="h-4 w-4" />
                            <Skeleton className="h-4 w-32" />
                          </div>
                          <Skeleton className="h-3 w-48 mb-2" />
                          <div className="flex items-center gap-1.5 mt-2">
                            <Skeleton className="h-6 w-6 rounded" />
                            <Skeleton className="h-6 w-6 rounded" />
                            <Skeleton className="h-6 w-6 rounded" />
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Skeleton className="h-8 w-8" />
                          <Skeleton className="h-8 w-8" />
                          <Skeleton className="h-8 w-8" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Add Product Dialog ── */}
      <Dialog open={showAddProduct} onOpenChange={setShowAddProduct}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-base">Add Product to {selectedCategory?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold">Product Registry Item <span className="text-destructive">*</span></Label>
              <Select
                value={newProduct.registryProductId}
                onValueChange={(value) => setNewProduct((prev) => ({ ...prev, registryProductId: value }))}
              >
                <SelectTrigger className="mt-1 h-9 text-sm">
                  <SelectValue placeholder="Select from Product Registry" />
                </SelectTrigger>
                <SelectContent>
                  {registrySelectionOptions.map((item) => (
                    <SelectItem key={item.productId} value={item.productId}>
                      {item.productName} · {item.sku}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedRegistryProductForDraft && (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  SKU: <span className="font-mono">{selectedRegistryProductForDraft.sku}</span> · Vendor: {selectedRegistryProductForDraft.vendor || "Unassigned"}
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs font-semibold">Product Name <span className="text-destructive">*</span></Label>
              <Input className="mt-1 h-9 text-sm" value={newProduct.displayName}
                onChange={e => setNewProduct(prev => ({ ...prev, displayName: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Product Type</Label>
                <Select
                  value={newProduct.productKind}
                  onValueChange={(value: "single" | "set" | "bundle") =>
                    setNewProduct((prev) => ({ ...prev, productKind: value }))
                  }
                >
                  <SelectTrigger className="mt-1 h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Single</SelectItem>
                    <SelectItem value="set">Set</SelectItem>
                    <SelectItem value="bundle">Bundle</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold">Pricing Impact</Label>
                <Input className="mt-1 h-9 text-sm" placeholder="e.g. +$200 or Included"
                  value={newProduct.pricingImpact} onChange={e => setNewProduct(prev => ({ ...prev, pricingImpact: e.target.value }))} />
              </div>
              <div className="col-span-2">
                <Label className="text-xs font-semibold">Vendor (optional)</Label>
                <Input className="mt-1 h-9 text-sm" value={newProduct.vendor}
                  onChange={e => setNewProduct(prev => ({ ...prev, vendor: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold">Material Filter (optional)</Label>
              <Input className="mt-1 h-9 text-sm" placeholder="e.g. Sentrel, Dekton"
                value={newProduct.materialFilter} onChange={e => setNewProduct(prev => ({ ...prev, materialFilter: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs font-semibold">Notes</Label>
              <Textarea className="mt-1 text-sm min-h-[60px]" value={newProduct.notes}
                onChange={e => setNewProduct(prev => ({ ...prev, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowAddProduct(false)}>Cancel</Button>
            <Button size="sm" onClick={handleAddProduct} disabled={!newProduct.registryProductId || !newProduct.displayName.trim()}>Add Product</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Edit Category Dialog ── */}
      <Dialog open={!!editingCategory} onOpenChange={(open) => { if (!open) setEditingCategory(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">Edit Category</DialogTitle>
          </DialogHeader>
          {editingCategory && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-semibold">Name <span className="text-destructive">*</span></Label>
                <Input
                  className="mt-1 h-9 text-sm"
                  value={editingCategory.name}
                  onChange={(e) => setEditingCategory((prev) => (prev ? { ...prev, name: e.target.value } : prev))}
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">Slug</Label>
                <Input
                  className="mt-1 h-9 text-sm font-mono"
                  value={editingCategory.slug}
                  onChange={(e) => setEditingCategory((prev) => (prev ? { ...prev, slug: e.target.value } : prev))}
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">Description</Label>
                <Textarea
                  className="mt-1 text-sm min-h-[72px]"
                  value={editingCategory.description}
                  onChange={(e) => setEditingCategory((prev) => (prev ? { ...prev, description: e.target.value } : prev))}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold">Icon</Label>
                  <Input
                    className="mt-1 h-9 text-sm"
                    value={editingCategory.icon}
                    onChange={(e) => setEditingCategory((prev) => (prev ? { ...prev, icon: e.target.value } : prev))}
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Sort Order</Label>
                  <Input
                    className="mt-1 h-9 text-sm"
                    value={editingCategory.sortOrder}
                    onChange={(e) => setEditingCategory((prev) => (prev ? { ...prev, sortOrder: e.target.value } : prev))}
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingCategory(null)}>Cancel</Button>
            <Button onClick={handleUpdateCategory} disabled={isLoading || !editingCategory?.name.trim()}>
              Save Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Category Confirm ── */}
      <AlertDialog open={!!deletingCategory} onOpenChange={(open) => { if (!open) setDeletingCategory(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete category?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. You can only delete categories with no products.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletingCategory(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCategory} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Add Category Dialog ── */}
      <Dialog open={showAddCategory} onOpenChange={setShowAddCategory}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">Add Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold">Name <span className="text-destructive">*</span></Label>
              <Input
                className="mt-1 h-9 text-sm"
                value={newCategory.name}
                onChange={(e) => setNewCategory((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Shower Benches"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Slug</Label>
              <Input
                className="mt-1 h-9 text-sm font-mono"
                value={newCategory.slug}
                onChange={(e) => setNewCategory((prev) => ({ ...prev, slug: e.target.value }))}
                placeholder="auto-from-name if blank"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Description</Label>
              <Textarea
                className="mt-1 text-sm min-h-[72px]"
                value={newCategory.description}
                onChange={(e) => setNewCategory((prev) => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Icon</Label>
                <Input
                  className="mt-1 h-9 text-sm"
                  value={newCategory.icon}
                  onChange={(e) => setNewCategory((prev) => ({ ...prev, icon: e.target.value }))}
                  placeholder="Package"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">Sort Order</Label>
                <Input
                  className="mt-1 h-9 text-sm"
                  value={newCategory.sortOrder}
                  onChange={(e) => setNewCategory((prev) => ({ ...prev, sortOrder: e.target.value }))}
                  placeholder="Auto"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCategory(false)}>Cancel</Button>
            <Button onClick={handleAddCategory} disabled={isLoading || !newCategory.name.trim()}>
              Create Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Edit Product Dialog ── */}
      <Dialog open={!!editingProduct} onOpenChange={() => setEditingProduct(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-base">Edit Product</DialogTitle></DialogHeader>
          {editingProduct && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-semibold">Product Name</Label>
                <Input className="mt-1 h-9 text-sm" value={editingProduct.displayName}
                  onChange={e => setEditingProduct(prev => prev ? { ...prev, displayName: e.target.value } : null)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold">Product Type</Label>
                  <Select
                    value={editingProduct.productKind || "single"}
                    onValueChange={(value: "single" | "set" | "bundle") =>
                      setEditingProduct((prev) => (prev ? { ...prev, productKind: value } : prev))
                    }
                  >
                    <SelectTrigger className="mt-1 h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single</SelectItem>
                      <SelectItem value="set">Set</SelectItem>
                      <SelectItem value="bundle">Bundle</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Pricing Impact</Label>
                  <Input className="mt-1 h-9 text-sm" value={editingProduct.pricingImpact}
                    onChange={e => setEditingProduct(prev => prev ? { ...prev, pricingImpact: e.target.value } : null)} />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs font-semibold">Vendor</Label>
                  <Input className="mt-1 h-9 text-sm" value={editingProduct.vendor}
                    onChange={e => setEditingProduct(prev => prev ? { ...prev, vendor: e.target.value } : null)} />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold">Material Filter</Label>
                <Input className="mt-1 h-9 text-sm" value={editingProduct.materialFilter || ""}
                  onChange={e => setEditingProduct(prev => prev ? { ...prev, materialFilter: e.target.value || undefined } : null)} />
              </div>
              <div>
                <Label className="text-xs font-semibold">Notes</Label>
                <Textarea className="mt-1 text-sm min-h-[60px]" value={editingProduct.notes}
                  onChange={e => setEditingProduct(prev => prev ? { ...prev, notes: e.target.value } : null)} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setEditingProduct(null)}>Cancel</Button>
            <Button size="sm" onClick={handleUpdateProduct}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Variants Manager Dialog (Enhanced) ── */}
      <Dialog open={!!editingVariant} onOpenChange={() => setEditingVariant(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <Layers className="h-4 w-4 text-primary" />
              Variants — {editingVariant?.product.displayName}
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              Manage colors, styles, images (Display + Reference), SKUs, and pricing.
            </p>
          </DialogHeader>
          {editingVariant && (
            <ScrollArea className="flex-1 -mx-6 px-6">
              <div className="space-y-3">
                {/* Existing variants */}
                {(() => {
                  const currentProduct = products.find(p => p.id === editingVariant.product.id);
                  const isComposite = currentProduct?.productKind === "set" || currentProduct?.productKind === "bundle";
                  const variants = currentProduct?.variants || [];
                  if (variants.length === 0) return (
                    <div className="text-center py-8 text-muted-foreground">
                      <Image className="h-8 w-8 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">No variants yet</p>
                      <p className="text-xs mt-1">Add color options, styles, or configurations below.</p>
                    </div>
                  );
                  return variants.sort((a, b) => a.sortOrder - b.sortOrder).map(v => (
                    <Card key={v.id} className={`transition-all ${!v.active ? "opacity-50" : ""}`}>
                      <CardContent className="p-3">
                        <div className="flex items-start gap-3">
                          {/* Dual Images */}
                          <div className="flex gap-2 shrink-0">
                            <div className="text-center">
                              <div className="h-14 w-14 rounded-lg border border-border bg-muted flex items-center justify-center overflow-hidden">
                                {v.imageUrl ? (
                                  <img src={v.imageUrl} alt={v.name} className="h-full w-full object-cover" />
                                ) : (
                                  <Eye className="h-4 w-4 text-muted-foreground" />
                                )}
                              </div>
                              <span className="text-[8px] text-muted-foreground mt-0.5 block">Display</span>
                            </div>
                            <div className="text-center">
                              <div className="h-14 w-14 rounded-lg border border-dashed border-border bg-muted/50 flex items-center justify-center overflow-hidden">
                                {v.referenceImageUrl ? (
                                  <img src={v.referenceImageUrl} alt={`${v.name} ref`} className="h-full w-full object-cover" />
                                ) : (
                                  <WarehouseIcon className="h-4 w-4 text-muted-foreground/50" />
                                )}
                              </div>
                              <span className="text-[8px] text-muted-foreground mt-0.5 block">Reference</span>
                            </div>
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0 space-y-1.5">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold">{v.name}</span>
                              {v.sku && <Badge variant="outline" className="text-[8px] h-4">SKU: {v.sku}</Badge>}
                              {v.items.length > 0 && (
                                <Badge variant="outline" className="text-[8px] h-4">
                                  {v.items.reduce((sum, item) => sum + item.qty, 0)} items
                                </Badge>
                              )}
                              {v.vendor && <Badge variant="secondary" className="text-[8px] h-4">{v.vendor}</Badge>}
                              {v.basePrice !== null && <Badge variant="outline" className="text-[8px] h-4">Base: ${v.basePrice}</Badge>}
                              {v.cost !== null && hasPerm("settings_edit") && (
                                <Badge variant="secondary" className="text-[8px] h-4">Cost: ${v.cost}</Badge>
                              )}
                            </div>
                            {v.description && <p className="text-[10px] text-muted-foreground">{v.description}</p>}

                            {/* Inline editable fields */}
                            <div className="grid grid-cols-2 gap-1.5">
                              <Input className="h-6 text-[10px]" placeholder="Display Image URL"
                                value={v.imageUrl} onChange={e => updateVariantField(editingVariant.product.id, v.id, "imageUrl", e.target.value)} />
                              <Input className="h-6 text-[10px]" placeholder="Reference Image URL"
                                value={v.referenceImageUrl} onChange={e => updateVariantField(editingVariant.product.id, v.id, "referenceImageUrl", e.target.value)} />
                              {isComposite ? (
                                <Input
                                  className="h-6 text-[10px]"
                                  value={v.sku || buildGeneratedVariantSku(editingVariant.product.displayName, v.name)}
                                  readOnly
                                />
                              ) : (
                                <Popover
                                  open={!!variantSkuPickerOpen[v.id]}
                                  onOpenChange={(open) => setVariantSkuPickerOpen((prev) => ({ ...prev, [v.id]: open }))}
                                >
                                  <PopoverTrigger asChild>
                                    <Button variant="outline" role="combobox" className="h-6 text-[10px] justify-between px-2">
                                      {v.sku ? `${v.sku}` : "Select SKU"}
                                      <ChevronsUpDown className="ml-1 h-3 w-3 opacity-60" />
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="z-[1200] w-[360px] p-0" align="start">
                                    <Command className="overflow-visible [&_[cmdk-list]]:pt-1 [&_[cmdk-list]]:scroll-pt-14 [&_[cmdk-item]]:scroll-mt-14">
                                        <CommandInput placeholder="Search SKU or name..." />
                                        <CommandList className="pt-2 max-h-[260px] overflow-y-scroll pointer-events-auto [scroll-padding-top:3.5rem]">
                                        <CommandEmpty>No SKU found.</CommandEmpty>
                                        <CommandGroup>
                                          <CommandItem
                                            value="__none no sku"
                                            onSelect={() => {
                                              applyRegistrySkuToVariant(editingVariant.product.id, v.id, "");
                                              setVariantSkuPickerOpen((prev) => ({ ...prev, [v.id]: false }));
                                            }}
                                          >
                                            <Check className={`mr-2 h-3.5 w-3.5 ${!v.sku ? "opacity-100" : "opacity-0"}`} />
                                            No SKU
                                          </CommandItem>
                                          {activeRegistryProducts.map((registryProduct) => (
                                            <CommandItem
                                              key={registryProduct.productId}
                                              value={`${registryProduct.sku} ${registryProduct.productName} ${registryProduct.vendor || ""}`}
                                              onSelect={() => {
                                                applyRegistrySkuToVariant(editingVariant.product.id, v.id, registryProduct.sku);
                                                setVariantSkuPickerOpen((prev) => ({ ...prev, [v.id]: false }));
                                              }}
                                            >
                                              <Check className={`mr-2 h-3.5 w-3.5 ${v.sku === registryProduct.sku ? "opacity-100" : "opacity-0"}`} />
                                              {registryProduct.sku} · {registryProduct.productName}
                                            </CommandItem>
                                          ))}
                                        </CommandGroup>
                                      </CommandList>
                                    </Command>
                                  </PopoverContent>
                                </Popover>
                              )}
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-6 text-[10px]"
                                onClick={() => startCreateRegistrySku({ productId: editingVariant.product.id, variantId: v.id }, v.name, v.sku)}
                              >
                                Create SKU
                              </Button>
                              <Input className="h-6 text-[10px]" placeholder="Vendor override"
                                value={v.vendor} onChange={e => updateVariantField(editingVariant.product.id, v.id, "vendor", e.target.value)} />
                              <Input className="h-6 text-[10px]" placeholder="Base Price ($)" type="number"
                                value={v.basePrice ?? ""} onChange={e => updateVariantField(editingVariant.product.id, v.id, "basePrice", e.target.value ? parseFloat(e.target.value) : null)} />
                              {hasPerm("settings_edit") && (
                                <Input className="h-6 text-[10px]" placeholder="Cost ($)" type="number"
                                  value={v.cost ?? ""} onChange={e => updateVariantField(editingVariant.product.id, v.id, "cost", e.target.value ? parseFloat(e.target.value) : null)} />
                              )}
                              <div className="col-span-2 space-y-1">
                                <div className="flex items-center gap-1.5">
                                  <Popover
                                    open={!!variantItemPickerOpen[v.id]}
                                    onOpenChange={(open) => setVariantItemPickerOpen((prev) => ({ ...prev, [v.id]: open }))}
                                  >
                                    <PopoverTrigger asChild>
                                      <Button variant="outline" role="combobox" className="h-6 text-[10px] justify-between px-2 flex-1">
                                        {variantSelectedItemSku[v.id] && variantSelectedItemSku[v.id] !== "__none"
                                          ? variantSelectedItemSku[v.id]
                                          : "Add item from Product Registry"}
                                        <ChevronsUpDown className="ml-1 h-3 w-3 opacity-60" />
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="z-[1200] w-[360px] p-0" align="start">
                                      <Command className="overflow-visible [&_[cmdk-list]]:pt-1 [&_[cmdk-list]]:scroll-pt-14 [&_[cmdk-item]]:scroll-mt-14">
                                        <CommandInput placeholder="Search item SKU or name..." />
                                        <CommandList className="pt-2 max-h-[260px] overflow-y-scroll pointer-events-auto [scroll-padding-top:3.5rem]">
                                          <CommandEmpty>No item found.</CommandEmpty>
                                          <CommandGroup>
                                            {activeRegistryProducts.map((registryProduct) => (
                                              <CommandItem
                                                key={registryProduct.productId}
                                                value={`${registryProduct.sku} ${registryProduct.productName} ${registryProduct.vendor || ""}`}
                                                onSelect={() => {
                                                  setVariantSelectedItemSku((prev) => ({ ...prev, [v.id]: registryProduct.sku }));
                                                  setVariantItemPickerOpen((prev) => ({ ...prev, [v.id]: false }));
                                                }}
                                              >
                                                <Check
                                                  className={`mr-2 h-3.5 w-3.5 ${
                                                    variantSelectedItemSku[v.id] === registryProduct.sku ? "opacity-100" : "opacity-0"
                                                  }`}
                                                />
                                                {registryProduct.sku} · {registryProduct.productName}
                                              </CommandItem>
                                            ))}
                                          </CommandGroup>
                                        </CommandList>
                                      </Command>
                                    </PopoverContent>
                                  </Popover>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-6 text-[10px]"
                                    onClick={() => addItemToVariant(editingVariant.product.id, v.id)}
                                  >
                                    Add item
                                  </Button>
                                </div>
                                {v.items.length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    {v.items.map((item) => {
                                      const registryItem = registryBySku.get(item.sku);
                                      const itemLabel = registryItem?.productName || item.sku;
                                      const sameAsSku =
                                        itemLabel.trim().toUpperCase() === item.sku.trim().toUpperCase();
                                      return (
                                        <Badge
                                          key={item.sku}
                                          variant="outline"
                                          className="text-[9px] h-5 gap-1"
                                          title={`${itemLabel} (${item.sku})`}
                                        >
                                          {sameAsSku ? (
                                            <span className="font-mono">{item.sku}</span>
                                          ) : (
                                            <>
                                              <span className="truncate max-w-[180px]">{itemLabel}</span>
                                              <span className="text-muted-foreground/70">·</span>
                                              <span className="font-mono">{item.sku}</span>
                                            </>
                                          )}
                                          <button type="button" className="px-1" onClick={() => adjustVariantItemQty(editingVariant.product.id, v.id, item.sku, -1)}>−</button>
                                          <span>{item.qty}</span>
                                          <button type="button" className="px-1" onClick={() => adjustVariantItemQty(editingVariant.product.id, v.id, item.sku, 1)}>+</button>
                                          <button type="button" className="px-1" onClick={() => removeItemFromVariant(editingVariant.product.id, v.id, item.sku)}>×</button>
                                        </Badge>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Controls */}
                          <div className="flex items-center gap-1.5 shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-[10px]"
                              onClick={() => persistVariantItems(editingVariant.product.id, v.id)}
                            >
                              Save items
                            </Button>
                            <Switch checked={v.active} onCheckedChange={() => toggleVariantActive(editingVariant.product.id, v.id, v.active)} />
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive">
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Variant?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete "{v.name}"? This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDeleteVariant(editingVariant.product.id, v.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ));
                })()}

                {/* Add variant form */}
                <Card className="border-dashed">
                  <CardContent className="p-3">
                    <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Add Variant</p>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-[10px] text-muted-foreground">Name *</Label>
                        <Input className="mt-0.5 h-7 text-xs" placeholder="e.g. Kelya"
                          value={newVariant.name} onChange={e => setNewVariant(prev => ({ ...prev, name: e.target.value }))} />
                      </div>
                      <div>
                        <Label className="text-[10px] text-muted-foreground">Display Image URL</Label>
                        <Input className="mt-0.5 h-7 text-xs" placeholder="https://..."
                          value={newVariant.imageUrl} onChange={e => setNewVariant(prev => ({ ...prev, imageUrl: e.target.value }))} />
                      </div>
                      <div>
                        <Label className="text-[10px] text-muted-foreground">Reference Image URL</Label>
                        <Input className="mt-0.5 h-7 text-xs" placeholder="https://..."
                          value={newVariant.referenceImageUrl} onChange={e => setNewVariant(prev => ({ ...prev, referenceImageUrl: e.target.value }))} />
                      </div>
                      <div>
                        <Label className="text-[10px] text-muted-foreground">SKU</Label>
                        {editingVariant?.product.productKind === "set" || editingVariant?.product.productKind === "bundle" ? (
                          <Input
                            className="mt-0.5 h-7 text-xs"
                            value={newVariant.sku || buildGeneratedVariantSku(editingVariant?.product.displayName || "", newVariant.name)}
                            readOnly
                          />
                        ) : (
                          <Popover open={newVariantSkuPickerOpen} onOpenChange={setNewVariantSkuPickerOpen}>
                            <PopoverTrigger asChild>
                              <Button variant="outline" role="combobox" className="mt-0.5 h-7 text-xs justify-between w-full px-2">
                                {newVariant.sku || "Select SKU from registry"}
                                <ChevronsUpDown className="ml-1 h-3 w-3 opacity-60" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="z-[1200] w-[360px] p-0" align="start">
                              <Command className="overflow-visible [&_[cmdk-list]]:pt-1 [&_[cmdk-list]]:scroll-pt-14 [&_[cmdk-item]]:scroll-mt-14">
                                <CommandInput placeholder="Search SKU or name..." />
                                <CommandList className="pt-2 max-h-[260px] overflow-y-scroll pointer-events-auto [scroll-padding-top:3.5rem]">
                                  <CommandEmpty>No SKU found.</CommandEmpty>
                                  <CommandGroup>
                                    <CommandItem
                                      value="__none no sku"
                                      onSelect={() => {
                                        setNewVariant((prev) => ({ ...prev, sku: "" }));
                                        setNewVariantSkuPickerOpen(false);
                                      }}
                                    >
                                      <Check className={`mr-2 h-3.5 w-3.5 ${!newVariant.sku ? "opacity-100" : "opacity-0"}`} />
                                      No SKU
                                    </CommandItem>
                                    {activeRegistryProducts.map((registryProduct) => (
                                      <CommandItem
                                        key={registryProduct.productId}
                                        value={`${registryProduct.sku} ${registryProduct.productName} ${registryProduct.vendor || ""}`}
                                        onSelect={() => {
                                          setNewVariant((prev) => ({
                                            ...prev,
                                            sku: registryProduct.sku,
                                            vendor: registryProduct.vendor || prev.vendor,
                                            basePrice: String(registryProduct.price),
                                            cost: String(registryProduct.cost),
                                          }));
                                          setNewVariantSkuPickerOpen(false);
                                        }}
                                      >
                                        <Check className={`mr-2 h-3.5 w-3.5 ${newVariant.sku === registryProduct.sku ? "opacity-100" : "opacity-0"}`} />
                                        {registryProduct.sku} · {registryProduct.productName}
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="mt-1 h-6 text-[10px]"
                          onClick={() => startCreateRegistrySku({ productId: editingVariant?.product.id || "", variantId: "__new__" }, newVariant.name, newVariant.sku)}
                          disabled={!editingVariant}
                        >
                          SKU not listed? Create it
                        </Button>
                      </div>
                      <div>
                        <Label className="text-[10px] text-muted-foreground">Vendor</Label>
                        <Input className="mt-0.5 h-7 text-xs" placeholder="Inherits if blank"
                          value={newVariant.vendor} onChange={e => setNewVariant(prev => ({ ...prev, vendor: e.target.value }))} />
                      </div>
                      <div>
                        <Label className="text-[10px] text-muted-foreground">Base Price ($)</Label>
                        <Input className="mt-0.5 h-7 text-xs" placeholder="Retail price" type="number"
                          value={newVariant.basePrice}
                          onChange={e =>
                            setNewVariant(prev => ({
                              ...prev,
                              basePrice: e.target.value,
                              cost: prev.cost || e.target.value,
                            }))
                          }
                        />
                      </div>
                      {hasPerm("settings_edit") && (
                        <div>
                          <Label className="text-[10px] text-muted-foreground">Cost ($)</Label>
                          <Input className="mt-0.5 h-7 text-xs" placeholder="Internal cost" type="number"
                            value={newVariant.cost} onChange={e => setNewVariant(prev => ({ ...prev, cost: e.target.value }))} />
                        </div>
                      )}
                    </div>
                    <div className="mt-2">
                      <Label className="text-[10px] text-muted-foreground">Description</Label>
                      <Input className="mt-0.5 h-7 text-xs" placeholder="Optional notes..."
                        value={newVariant.description} onChange={e => setNewVariant(prev => ({ ...prev, description: e.target.value }))} />
                    </div>
                    <div className="mt-2 space-y-1">
                      <Label className="text-[10px] text-muted-foreground">Items (Product Registry)</Label>
                      <div className="flex items-center gap-1.5">
                        <Popover open={newVariantItemPickerOpen} onOpenChange={setNewVariantItemPickerOpen}>
                          <PopoverTrigger asChild>
                            <Button variant="outline" role="combobox" className="h-7 text-xs justify-between flex-1 px-2">
                              {newVariant.selectedItemSku !== "__none" ? newVariant.selectedItemSku : "Select item SKU"}
                              <ChevronsUpDown className="ml-1 h-3 w-3 opacity-60" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="z-[1200] w-[360px] p-0" align="start">
                            <Command className="overflow-visible [&_[cmdk-list]]:pt-1 [&_[cmdk-list]]:scroll-pt-14 [&_[cmdk-item]]:scroll-mt-14">
                              <CommandInput placeholder="Search item SKU or name..." />
                              <CommandList className="pt-2 max-h-[260px] overflow-y-scroll pointer-events-auto [scroll-padding-top:3.5rem]">
                                <CommandEmpty>No item found.</CommandEmpty>
                                <CommandGroup>
                                  {activeRegistryProducts.map((registryProduct) => (
                                    <CommandItem
                                      key={registryProduct.productId}
                                      value={`${registryProduct.sku} ${registryProduct.productName} ${registryProduct.vendor || ""}`}
                                      onSelect={() => {
                                        setNewVariant((prev) => ({ ...prev, selectedItemSku: registryProduct.sku }));
                                        setNewVariantItemPickerOpen(false);
                                      }}
                                    >
                                      <Check
                                        className={`mr-2 h-3.5 w-3.5 ${
                                          newVariant.selectedItemSku === registryProduct.sku ? "opacity-100" : "opacity-0"
                                        }`}
                                      />
                                      {registryProduct.sku} · {registryProduct.productName}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                        <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={addItemToNewVariant}>
                          Add item
                        </Button>
                      </div>
                      {newVariant.items.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {newVariant.items.map((item) => {
                            const registryItem = registryBySku.get(item.sku);
                            const itemLabel = registryItem?.productName || item.sku;
                            const sameAsSku =
                              itemLabel.trim().toUpperCase() === item.sku.trim().toUpperCase();
                            return (
                              <Badge
                                key={item.sku}
                                variant="outline"
                                className="text-[9px] h-5 gap-1"
                                title={`${itemLabel} (${item.sku})`}
                              >
                                {sameAsSku ? (
                                  <span className="font-mono">{item.sku}</span>
                                ) : (
                                  <>
                                    <span className="truncate max-w-[180px]">{itemLabel}</span>
                                    <span className="text-muted-foreground/70">·</span>
                                    <span className="font-mono">{item.sku}</span>
                                  </>
                                )}
                                <button type="button" className="px-1" onClick={() => adjustNewVariantItemQty(item.sku, -1)}>−</button>
                                <span>{item.qty}</span>
                                <button type="button" className="px-1" onClick={() => adjustNewVariantItemQty(item.sku, 1)}>+</button>
                                <button type="button" className="px-1" onClick={() => removeItemFromNewVariant(item.sku)}>×</button>
                              </Badge>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <Button size="sm" className="h-7 text-xs gap-1 mt-2" onClick={handleAddVariant} disabled={!newVariant.name.trim()}>
                      <Plus className="h-3 w-3" /> Add Variant
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </ScrollArea>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setEditingVariant(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── CSV Import Wizard ── */}
      <CatalogImportWizard 
        open={showImportWizard} 
        onOpenChange={setShowImportWizard} 
        importBatches={importBatches}
        setImportBatches={setImportBatches}
      />

      {/* ── Import History ── */}
      <ImportHistoryDialog
        open={showImportHistory}
        onOpenChange={setShowImportHistory}
        batches={importBatches}
        onRevert={(id) => {
          const batch = importBatches.find(b => b.id === id);
          if (!batch) return;
          try {
            const snapshot = JSON.parse(batch.snapshotBefore);
            setProducts(snapshot);
            setImportBatches(prev => prev.map(b => b.id === id ? { ...b, committed: false } : b));
          } catch { /* noop */ }
        }}
      />

      <Dialog open={showCreateRegistrySku} onOpenChange={setShowCreateRegistrySku}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Product Registry SKU</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">SKU *</Label>
              <Input
                className="mt-1 h-8 text-xs"
                value={registrySkuDraft.sku}
                onChange={(e) => setRegistrySkuDraft((prev) => ({ ...prev, sku: e.target.value.toUpperCase() }))}
              />
            </div>
            <div>
              <Label className="text-xs">Product Name *</Label>
              <Input
                className="mt-1 h-8 text-xs"
                value={registrySkuDraft.productName}
                onChange={(e) => setRegistrySkuDraft((prev) => ({ ...prev, productName: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs">Vendor</Label>
              <Input
                className="mt-1 h-8 text-xs"
                value={registrySkuDraft.vendor}
                onChange={(e) => setRegistrySkuDraft((prev) => ({ ...prev, vendor: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs">Category</Label>
              <Select
                value={registrySkuDraft.category}
                onValueChange={(value) => setRegistrySkuDraft((prev) => ({ ...prev, category: value as ProductCategory }))}
              >
                <SelectTrigger className="mt-1 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Wall System">Wall System</SelectItem>
                  <SelectItem value="Base">Base</SelectItem>
                  <SelectItem value="Glass/Enclosure">Glass/Enclosure</SelectItem>
                  <SelectItem value="Hardware">Hardware</SelectItem>
                  <SelectItem value="Add-On">Add-On</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Cost ($)</Label>
              <Input
                className="mt-1 h-8 text-xs"
                type="number"
                value={registrySkuDraft.cost}
                onChange={(e) => setRegistrySkuDraft((prev) => ({ ...prev, cost: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs">Price ($)</Label>
              <Input
                className="mt-1 h-8 text-xs"
                type="number"
                value={registrySkuDraft.price}
                onChange={(e) => setRegistrySkuDraft((prev) => ({ ...prev, price: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateRegistrySku(false)}>Cancel</Button>
            <Button onClick={handleCreateRegistrySku} disabled={isLoading || !registrySkuDraft.sku.trim() || !registrySkuDraft.productName.trim()}>
              Create SKU
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
